import React, { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import Home from './components/Home';
import HolidaysPage from './components/HolidaysPage';
import HolidayDetail from './components/HolidayDetail';
import ShoppingList from './components/ShoppingList';
import TodoList from './components/TodoList';
import CalendarPage from './components/CalendarPage';
import HolidayForm from './components/HolidayForm';
import ExpenseForm from './components/ExpenseForm';
import MembersManager from './components/MembersManager';
import SignIn from './components/SignIn';
import { loadData, saveData, googleSignIn, checkAuth, signOut } from './utils/storage';
import { registerServiceWorker, subscribeToPush } from './utils/push';
import { createSampleData } from './utils/sampleData';
import { getTripTotals, generateId } from './utils/helpers';

function fireConfetti() {
  const duration = 2000;
  const end = Date.now() + duration;

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#a855f7', '#14b8a6', '#f97316', '#eab308'],
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#a855f7', '#14b8a6', '#f97316', '#eab308'],
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  };
  frame();
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const [holidays, setHolidays] = useState([]);
  const [members, setMembers] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [todos, setTodos] = useState([]);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [pageData, setPageData] = useState(null);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseHolidayId, setExpenseHolidayId] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const hasUserChanged = React.useRef(false);

  const navigate = (page, data = null) => {
    setCurrentPage(page);
    setPageData(data);
  };

  // Check for existing session on mount
  useEffect(() => {
    checkAuth().then(({ user }) => {
      if (user) setUser(user);
      setAuthLoading(false);
    }).catch(() => setAuthLoading(false));
  }, []);

  // Register the push service worker once signed in, and if notifications are
  // already granted, re-send the current subscription so the server always has
  // it (self-heals a subscription that was never stored server-side).
  useEffect(() => {
    if (!user) return;
    registerServiceWorker().then(() => {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        subscribeToPush().catch(() => {});
      }
    });
  }, [user]);

  // Load data once authenticated
  useEffect(() => {
    if (!user) return;
    loadData().then(data => {
      if (data && (data.holidays?.length > 0 || data.members?.length > 0)) {
        setHolidays(data.holidays || []);
        setMembers(data.members || []);
        setShoppingList(data.shoppingList || []);
        setTodos(data.todos || []);
      } else if (data) {
        const sample = createSampleData();
        setHolidays(sample.holidays);
        setMembers(sample.members);
      }
      setLoaded(true);
    });
  }, [user]);

  // Save on changes — but only after the user has made a change, not on initial load
  const persist = useCallback(() => {
    saveData({ holidays, members, shoppingList, todos });
  }, [holidays, members, shoppingList, todos]);

  useEffect(() => {
    if (!loaded) return;
    if (!hasUserChanged.current) {
      hasUserChanged.current = true;
      return;
    }
    if (holidays.length > 0 || members.length > 0 || shoppingList.length > 0 || todos.length > 0) {
      persist();
    }
  }, [persist, holidays, members, shoppingList, todos, loaded]);

  // Auth handlers
  const handleGoogleSignIn = async (code) => {
    try {
      const result = await googleSignIn(code);
      if (result.error) {
        setAuthError(
          result.error === 'Email not in allowlist'
            ? 'This Google account is not authorized for Strong Family.'
            : result.error
        );
        return;
      }
      setAuthError(null);
      setUser(result.user);
    } catch (e) {
      setAuthError('Sign-in failed. Please try again.');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
    setLoaded(false);
    setHolidays([]);
    setMembers([]);
    setShoppingList([]);
    setTodos([]);
    setCurrentPage('dashboard');
    setPageData(null);
    hasUserChanged.current = false;
  };

  // Holiday CRUD
  const saveHoliday = (holiday) => {
    setHolidays(prev => {
      const exists = prev.find(h => h.id === holiday.id);
      if (exists) {
        return prev.map(h => h.id === holiday.id ? holiday : h);
      }
      return [...prev, holiday];
    });
  };

  const deleteHoliday = (id) => {
    setHolidays(prev => prev.filter(h => h.id !== id));
    navigate('holidays');
  };

  const promoteToBooked = (id) => {
    setHolidays(prev => prev.map(h => h.id === id ? { ...h, status: 'booked' } : h));
  };

  // Expense operations
  const addExpense = (holidayId, expense) => {
    setHolidays(prev => prev.map(h =>
      h.id === holidayId ? { ...h, expenses: [...h.expenses, expense] } : h
    ));
  };

  const toggleExpensePaid = (holidayId, expenseId) => {
    setHolidays(prev => prev.map(h => {
      if (h.id !== holidayId) return h;

      const updated = {
        ...h,
        expenses: h.expenses.map(e =>
          e.id === expenseId
            ? { ...e, isPaid: !e.isPaid, paidDate: !e.isPaid ? new Date().toISOString().split('T')[0] : null }
            : e
        ),
      };

      const { progress } = getTripTotals(updated);
      const prevTotals = getTripTotals(h);
      if (progress >= 100 && prevTotals.progress < 100) {
        setTimeout(fireConfetti, 100);
      }

      return updated;
    }));
  };

  const deleteExpense = (holidayId, expenseId) => {
    setHolidays(prev => prev.map(h =>
      h.id === holidayId ? { ...h, expenses: h.expenses.filter(e => e.id !== expenseId) } : h
    ));
  };

  // Shopping list
  const addShoppingItem = (text, existingItem) => {
    if (existingItem) {
      // Restore a previously removed item (undo)
      setShoppingList(prev => [...prev, existingItem]);
      return;
    }
    const linkedMember = members.find(m => m.email === user?.email);
    const addedBy = linkedMember?.name || user?.name || null;
    setShoppingList(prev => [...prev, { id: generateId(), text, addedBy, addedAt: new Date().toISOString() }]);
  };

  const removeShoppingItem = (id) => {
    setShoppingList(prev => prev.filter(item => item.id !== id));
  };

  // Todos
  const addTodo = (text, assignedTo = [], deadline = null) => {
    const linkedMember = members.find(m => m.email === user?.email);
    const createdBy = linkedMember?.name || user?.name || null;
    setTodos(prev => [...prev, {
      id: generateId(),
      text,
      assignedTo,
      deadline,
      done: false,
      completedAt: null,
      createdAt: new Date().toISOString(),
      createdBy,
    }]);
  };

  const toggleTodo = (id) => {
    setTodos(prev => prev.map(t =>
      t.id === id
        ? { ...t, done: !t.done, completedAt: !t.done ? new Date().toISOString() : null }
        : t
    ));
  };

  const deleteTodo = (id) => {
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  // Members
  const saveMembers = (newMembers) => {
    setMembers(newMembers);
  };

  // Navigation helpers
  const openAddPayment = (holidayId) => {
    setExpenseHolidayId(holidayId);
    setShowExpenseForm(true);
  };

  const openEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setShowHolidayForm(true);
  };

  const openAddHoliday = () => {
    setEditingHoliday(null);
    setShowHolidayForm(true);
  };

  // Resolve current holiday for detail page
  const selectedHoliday = currentPage === 'holiday-detail' && pageData?.holidayId
    ? holidays.find(h => h.id === pageData.holidayId)
    : null;

  // Back button target
  const getBackPage = () => {
    if (currentPage === 'holiday-detail') return 'holidays';
    if (currentPage === 'calendar') return 'dashboard';
    return 'dashboard';
  };

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  // Not signed in
  if (!user) {
    return <SignIn onSuccess={handleGoogleSignIn} error={authError} />;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {currentPage !== 'dashboard' && (
              <button
                onClick={() => navigate(getBackPage())}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium mr-2"
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => navigate('dashboard')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <span className="text-2xl">🏠</span>
              <h1 className="text-xl font-extrabold bg-gradient-to-r from-purple-600 via-teal-500 to-coral-400 bg-clip-text text-transparent">
                Strong Family
              </h1>
            </button>
          </div>
          <div className="flex items-center gap-2">
            {user.picture && (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
            )}
            <button
              onClick={handleSignOut}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {currentPage === 'dashboard' && (
          <Home
            holidays={holidays}
            members={members}
            shoppingList={shoppingList}
            todos={todos}
            user={user}
            navigate={navigate}
            onManageMembers={() => setShowMembers(true)}
          />
        )}
        {currentPage === 'holidays' && (
          <HolidaysPage
            holidays={holidays}
            members={members}
            navigate={navigate}
            onAddPayment={openAddPayment}
            onAddHoliday={openAddHoliday}
          />
        )}
        {currentPage === 'holiday-detail' && selectedHoliday && (
          <HolidayDetail
            holiday={selectedHoliday}
            members={members}
            onBack={() => navigate('holidays')}
            onTogglePaid={toggleExpensePaid}
            onAddPayment={openAddPayment}
            onDeleteExpense={deleteExpense}
            onEdit={openEditHoliday}
            onPromoteToDream={promoteToBooked}
            onDeleteHoliday={deleteHoliday}
          />
        )}
        {currentPage === 'calendar' && (
          <CalendarPage />
        )}
        {currentPage === 'shopping' && (
          <ShoppingList
            items={shoppingList}
            onAdd={addShoppingItem}
            onRemove={removeShoppingItem}
            fullPage
          />
        )}
        {currentPage === 'todos' && (
          <TodoList
            todos={todos}
            members={members}
            user={user}
            onAdd={addTodo}
            onToggle={toggleTodo}
            onDelete={deleteTodo}
          />
        )}
      </main>

      {/* Modals */}
      <HolidayForm
        isOpen={showHolidayForm}
        onClose={() => { setShowHolidayForm(false); setEditingHoliday(null); }}
        onSave={saveHoliday}
        holiday={editingHoliday}
        members={members}
      />

      <ExpenseForm
        isOpen={showExpenseForm}
        onClose={() => { setShowExpenseForm(false); setExpenseHolidayId(null); }}
        onSave={addExpense}
        holiday={holidays.find(h => h.id === expenseHolidayId)}
        members={members}
      />

      <MembersManager
        isOpen={showMembers}
        onClose={() => setShowMembers(false)}
        members={members}
        onSave={saveMembers}
        holidays={holidays}
      />
    </div>
  );
}
